import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, FormikActions, FormikProps } from 'formik';

import { useSnackbarContext } from 'ui-lib/components/Snackbar/SnackbarProvider';
import { resetPassword } from 'web-shared/api';
import HttpError from 'web-shared/services/http-service/HttpError';

import {
  initialValues,
  resetPasswordFieldNameGenerator,
  ResetPasswordFormValues,
  validationSchema,
} from '../../const';

interface ResetPasswordErrors {
  reset_password_token?: string[];
  password?: string;
}

interface Props {
  children: (props: FormikProps<ResetPasswordFormValues>) => React.ReactNode;
  resetPasswordToken: string;
}

const { n } = resetPasswordFieldNameGenerator;

const ResetPasswordPageFormikWrapper = ({ children, resetPasswordToken }: Props) => {
  const navigate = useNavigate();
  const { showErrorSnackbar } = useSnackbarContext();

  const handleSubmit = async (
    values: ResetPasswordFormValues,
    { setFieldError, setSubmitting }: FormikActions<ResetPasswordFormValues>,
  ) => {
    try {
      await resetPassword({
        reset_password_token: resetPasswordToken,
        password: values.password,
      });

      navigate('/sign-in', {
        replace: true,
        state: { showPasswordResetSuccess: true },
      });
    } catch (error) {
      if (HttpError.isHttpError<ResetPasswordErrors>(error)) {
        showErrorSnackbar({ message: error.message });

        if (error.errors) {
          if (error.errors.reset_password_token) {
            setFieldError(n('password'), 'auth.validation_resetPasswordTokenExpired');
          } else if (error.errors.password) {
            setFieldError(n('password'), error.errors.password);
          }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {children}
    </Formik>
  );
};

export default ResetPasswordPageFormikWrapper;
